import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  MessageCircle,
  LifeBuoy,
  CalendarCheck,
  Megaphone,
  FileText,
  Globe,
  MessagesSquare,
  PenLine,
  FileSpreadsheet,
  Users,
  Webhook,
  Database,
  FolderOpen,
  Camera,
  ThumbsUp,
  Send,
  Check,
} from 'lucide-react';
import Reveal from './Reveal';
import { tokens } from '../theme';

/* ──────── Shared Warm Editorial styles ──────── */

const SAGE_BORDER = 'rgba(47, 93, 80, 0.25)';
const COPPER_BORDER = 'rgba(199, 91, 57, 0.25)';

const cardSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: tokens.bgPaper,
  border: `1px solid ${tokens.border}`,
  borderRadius: `${tokens.radiusCard}px`,
  boxShadow: tokens.shadowSoft,
  p: { xs: 3.25, md: 4.5 },
  transition: `box-shadow ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease`,
  '&:hover': {
    boxShadow: tokens.shadowLift,
    borderColor: '#D9CDBC',
  },
} as const;

const iconSquareSx = {
  width: 44,
  height: 44,
  borderRadius: `${tokens.radiusControl}px`,
  bgcolor: tokens.copperTint,
  color: tokens.copper,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const;

/** Integration chip — cream bg, hairline border, lucide icon + label. */
function IntegrationChip({
  icon: Icon,
  label,
}: {
  icon: React.ElementType;
  label: string;
}) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.9,
        bgcolor: tokens.bgDefault,
        border: `1px solid ${tokens.border}`,
        borderRadius: '999px',
        px: 1.6,
        py: 0.7,
        transition: `border-color ${tokens.motion.micro}ms ease`,
        '&:hover': { borderColor: tokens.copper },
      }}
    >
      <Icon size={15} strokeWidth={1.5} color={tokens.copper} style={{ flexShrink: 0 }} />
      <Typography
        sx={{
          fontSize: '0.78rem',
          fontWeight: 500,
          color: 'text.primary',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/* ──────── CSS illustrations for each feature ──────── */

function ConversationCompare() {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        width: '100%',
        maxWidth: 440,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}
    >
      {/* robotic chatbot — neutral */}
      <Box
        sx={{
          flex: 1,
          minWidth: 180,
          p: 2,
          borderRadius: `${tokens.radiusControl}px`,
          bgcolor: tokens.bgDefault,
          border: `1px solid ${tokens.border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 1,
          }}
        >
          Typical Chatbot
        </Typography>
        {['Hello. How can I help?', 'Please select an option:', '1. Sales  2. Support  3. Other'].map(
          (msg, i) => (
            <Box
              key={i}
              sx={{
                bgcolor: tokens.bgPaper,
                border: `1px solid ${tokens.border}`,
                px: 1.2,
                py: 0.5,
                borderRadius: '8px',
                mb: 0.6,
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.45 }}>
                {msg}
              </Typography>
            </Box>
          ),
        )}
        <Typography
          sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.8, fontStyle: 'italic' }}
        >
          Robotic, menu-driven
        </Typography>
      </Box>

      {/* CleoLead — sage (AI doing it right) */}
      <Box
        sx={{
          flex: 1,
          minWidth: 180,
          p: 2,
          borderRadius: `${tokens.radiusControl}px`,
          bgcolor: tokens.sageTint,
          border: `1px solid ${SAGE_BORDER}`,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: tokens.sage,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 1,
          }}
        >
          CleoLead AI
        </Typography>
        {[
          'Hi! Thanks for reaching out.',
          "I see you're looking at our premium package — great choice!",
          'Want me to send you a personalized quote?',
        ].map((msg, i) => (
          <Box
            key={i}
            sx={{
              bgcolor: tokens.bgPaper,
              border: `1px solid ${tokens.border}`,
              px: 1.2,
              py: 0.5,
              borderRadius: '8px',
              mb: 0.6,
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', color: 'text.primary', lineHeight: 1.45 }}>
              {msg}
            </Typography>
          </Box>
        ))}
        <Typography sx={{ fontSize: '0.75rem', color: tokens.sage, mt: 0.8, fontStyle: 'italic' }}>
          Natural, contextual, on-brand
        </Typography>
      </Box>
    </Box>
  );
}

function TrainingFlow() {
  const sources = [
    { label: 'PDFs & Docs', icon: FileText },
    { label: 'Website URLs', icon: Globe },
    { label: 'Past Conversations', icon: MessagesSquare },
    { label: 'Custom Q&A', icon: PenLine },
  ];
  return (
    <Box sx={{ width: '100%', maxWidth: 400 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, mb: 2.5 }}>
        {sources.map((s) => (
          <IntegrationChip key={s.label} icon={s.icon} label={s.label} />
        ))}
      </Box>
      {/* arrow down */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box component="svg" viewBox="0 0 24 40" sx={{ width: 24, display: 'block' }} aria-hidden="true">
          <line
            x1="12"
            y1="0"
            x2="12"
            y2="30"
            stroke={tokens.copper}
            strokeWidth="1.5"
            strokeDasharray="3 3"
          />
          <polygon points="7,28 12,38 17,28" fill={tokens.copper} />
        </Box>
      </Box>
      {/* trained agent — sage = AI acting */}
      <Box
        sx={{
          p: 2,
          borderRadius: `${tokens.radiusControl}px`,
          bgcolor: tokens.sageTint,
          border: `1px solid ${SAGE_BORDER}`,
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: tokens.fontDisplay,
            fontSize: '1rem',
            fontWeight: 600,
            color: tokens.sage,
          }}
        >
          Your Trained AI Agent
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.4 }}>
          Knows your business, speaks your language
        </Typography>
      </Box>
    </Box>
  );
}

function LeadExtraction() {
  const fields = [
    { label: 'Name', value: 'Sarah Miller', confidence: '98%' },
    { label: 'Email', value: 'sarah@example.com', confidence: '95%' },
    { label: 'Interest', value: 'Premium Package', confidence: '92%' },
    { label: 'Budget', value: '$2,000-3,000', confidence: '88%' },
    { label: 'Timeline', value: 'Next month', confidence: '90%' },
  ];
  return (
    <Box sx={{ width: '100%', maxWidth: 380 }}>
      <Box
        sx={{
          borderRadius: `${tokens.radiusControl}px`,
          border: `1px solid ${tokens.border}`,
          bgcolor: tokens.bgPaper,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.2,
            bgcolor: tokens.bgDefault,
            borderBottom: `1px solid ${tokens.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.sage, flexShrink: 0 }}
          />
          <Typography
            sx={{
              fontSize: '0.78rem',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            Extracted Lead Data
          </Typography>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: tokens.sage,
              ml: 'auto',
            }}
          >
            Auto-captured
          </Typography>
        </Box>
        {fields.map((f, i) => (
          <Box
            key={f.label}
            sx={{
              px: 2,
              py: 0.9,
              display: 'flex',
              alignItems: 'center',
              borderBottom:
                i < fields.length - 1 ? `1px solid ${tokens.border}` : 'none',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
                width: 64,
                flexShrink: 0,
              }}
            >
              {f.label}
            </Typography>
            <Typography
              sx={{ fontSize: '0.8rem', color: 'text.primary', fontWeight: 500, flex: 1 }}
            >
              {f.value}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: tokens.sage,
                fontWeight: 600,
              }}
            >
              {f.confidence}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ExportFlow() {
  const destinations = [
    { label: 'Google Sheets', icon: FileSpreadsheet },
    { label: 'HubSpot CRM', icon: Users },
    { label: 'Webhooks', icon: Webhook },
    { label: 'SQL Database', icon: Database },
    { label: 'CSV / JSON', icon: FolderOpen },
  ];
  return (
    <Box sx={{ width: '100%', maxWidth: 380 }}>
      {/* source */}
      <Box
        sx={{
          p: 1.5,
          borderRadius: `${tokens.radiusControl}px`,
          bgcolor: tokens.copperTint,
          border: `1px solid ${COPPER_BORDER}`,
          textAlign: 'center',
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: tokens.copperDark }}>
          CleoLead Lead Data
        </Typography>
      </Box>

      {/* lines */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box component="svg" viewBox="0 0 200 30" sx={{ width: 200, display: 'block' }} aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <line
              key={i}
              x1="100"
              y1="0"
              x2={20 + i * 40}
              y2="28"
              stroke={tokens.copper}
              strokeWidth="1"
              strokeDasharray="3 3"
              opacity="0.45"
            />
          ))}
        </Box>
      </Box>

      {/* destinations */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
        {destinations.map((d) => (
          <IntegrationChip key={d.label} icon={d.icon} label={d.label} />
        ))}
      </Box>
    </Box>
  );
}

function BroadcastVisual() {
  const recipients = [
    { label: '142 Instagram', icon: Camera },
    { label: '89 Facebook', icon: ThumbsUp },
    { label: '67 Telegram', icon: Send },
  ];
  return (
    <Box sx={{ width: '100%', maxWidth: 380 }}>
      {/* compose */}
      <Box
        sx={{
          p: 2,
          borderRadius: `${tokens.radiusControl}px`,
          bgcolor: tokens.bgDefault,
          border: `1px solid ${tokens.border}`,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            mb: 0.8,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          Broadcast Message
        </Typography>
        <Box
          sx={{
            p: 1.2,
            borderRadius: '8px',
            bgcolor: tokens.bgPaper,
            border: `1px solid ${tokens.border}`,
          }}
        >
          <Typography
            sx={{ fontSize: '0.78rem', color: 'text.primary', fontStyle: 'italic', lineHeight: 1.5 }}
          >
            "Hi {'{name}'}, we have an exclusive 20% off package just for you this weekend! Reply 'YES' to book..."
          </Typography>
        </Box>
      </Box>

      {/* recipients */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {recipients.map((r) => (
          <IntegrationChip key={r.label} icon={r.icon} label={r.label} />
        ))}
      </Box>
    </Box>
  );
}

/* ──────── Features data ──────── */

const features = [
  {
    tag: 'Sells',
    icon: TrendingUp,
    gridSize: 7,
    title: 'A sales agent that works the whole funnel',
    description:
      'Qualifies inbound leads, books meetings, runs multi-touch follow-up, and closes loops on Instagram, WhatsApp, SMS, phone, and email — in your voice, with you in full control of every interaction.',
    bullets: ['Lead qualification across every channel', 'Suggests products & sends payment links in chat', 'Multi-touch follow-up sequences', 'Review-before-send mode'],
    illustration: <ConversationCompare />,
  },
  {
    tag: 'Knows your business',
    icon: MessageCircle,
    gridSize: 5,
    title: 'Chat AI trained on your business',
    description:
      'Upload documents, point at your website, import real past conversations. The AI learns your products, policies, and tone — answering customer questions with the accuracy of a senior team member.',
    bullets: ['PDF, DOC, TXT upload', 'Website auto-crawling', 'Live feed: Facebook posts & Wix events sync in', 'Learn from real past chats'],
    illustration: <TrainingFlow />,
  },
  {
    tag: 'Supports',
    icon: LifeBuoy,
    gridSize: 5,
    title: 'Helpdesk that resolves, not deflects',
    description:
      'The AI handles support questions, refund triage, order tracking, and complaints across every channel — pulling answers from your knowledge base, escalating only when it should. Every contact is profiled and enriched in real time.',
    bullets: ['Multi-channel support inbox', 'Auto-enriched client profiles', 'Smart human handoff'],
    illustration: <LeadExtraction />,
  },
  {
    tag: 'Organizes',
    icon: CalendarCheck,
    gridSize: 7,
    title: 'Calendar, bookings, and data — handled',
    description:
      'Books reservations and meetings into Google or Microsoft Calendar, syncs Wix table reservations for restaurants, pushes lead data to Sheets, CRMs, and webhooks. The AI keeps your back office tidy without you touching it.',
    bullets: ['Google Calendar two-way sync', 'Wix table reservations sync', 'Forms: AI collects the data you choose', 'CRM / Sheets / webhook export'],
    illustration: <ExportFlow />,
  },
  {
    tag: 'Re-engages',
    icon: Megaphone,
    gridSize: 12,
    title: 'Broadcast and bring customers back',
    description:
      'Send personalized messages to opted-in customer segments across every platform. The AI rewrites your copy per channel, audience, and language — so the same campaign reads native on Instagram, SMS, and email.',
    bullets: ['Multi-platform broadcast', 'AI per-channel rewriting', 'Built-in lead generation + outreach', 'Engagement + reply analytics'],
    illustration: <BroadcastVisual />,
  },
];

export default function FeaturesSection() {
  return (
    <Box id="features" sx={{ py: { xs: 8, md: 13 } }}>
      <Container maxWidth="lg">
        <Reveal>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 7 } }}>
            <Typography variant="overline" sx={{ display: 'block', mb: 1.5 }}>
              Features
            </Typography>
            <Typography variant="h2" sx={{ mb: 2 }}>
              Everything You Need, Nothing You Don't
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', maxWidth: 560, mx: 'auto' }}
            >
              One AI runs the whole front office — sells on every channel, supports every customer, organizes the back office. Trained on your business.
            </Typography>
          </Box>
        </Reveal>

        <Grid container spacing={3}>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isFullWidth = feature.gridSize === 12;

            const textBlock = (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ mb: 3 }}
                >
                  <Box sx={iconSquareSx}>
                    <Icon size={22} strokeWidth={1.5} />
                  </Box>
                  <Chip
                    label={feature.tag}
                    size="small"
                    color="secondary"
                    sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                  />
                </Stack>
                <Typography variant="h3" sx={{ fontSize: '1.45rem', mb: 1.5 }}>
                  {feature.title}
                </Typography>
                <Typography
                  sx={{ fontSize: '0.95rem', lineHeight: 1.65, color: 'text.secondary', mb: 3 }}
                >
                  {feature.description}
                </Typography>
                <Box>
                  {feature.bullets.map((b) => (
                    <Box
                      key={b}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        py: 1.4,
                        borderTop: `1px solid ${tokens.border}`,
                      }}
                    >
                      <Check
                        size={17}
                        strokeWidth={1.5}
                        color={tokens.copper}
                        style={{ flexShrink: 0, marginTop: 2 }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          color: 'text.primary',
                          lineHeight: 1.5,
                        }}
                      >
                        {b}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            );

            return (
              <Grid key={feature.tag} size={{ xs: 12, md: feature.gridSize }} sx={{ display: 'flex' }}>
                <Reveal
                  delay={(index % 2) * tokens.motion.stagger}
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <Box component="article" sx={cardSx}>
                    {isFullWidth ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          gap: { xs: 4, md: 8 },
                          alignItems: { md: 'center' },
                        }}
                      >
                        <Box sx={{ flex: 1 }}>{textBlock}</Box>
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            justifyContent: { xs: 'flex-start', md: 'center' },
                          }}
                        >
                          {feature.illustration}
                        </Box>
                      </Box>
                    ) : (
                      <>
                        {textBlock}
                        <Box sx={{ mt: 'auto', pt: 3.5 }}>{feature.illustration}</Box>
                      </>
                    )}
                  </Box>
                </Reveal>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}
